import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, FormProvider } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import { Box, Divider, MenuItem, TextField, Typography, Switch, FormControlLabel } from '@mui/material';

import { SettingsFields } from '@bandido/shared/types';

import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';
import ControlledPasswordField from '@frontend/features/common/components/ControlledPasswordField';
import { isSettingSensitive } from '@frontend/features/common/utils/helpers';
import { ControlledSelect } from '@frontend/features/common/components/ControlledSelect';

import { MultiSelect } from './components/MultiSelect';
import { MultiSelectItem } from './components/types';

interface SettingsProps {
  title: string;
  agencyId: number;
  fields: SettingsFields[];
  options?: {
    fieldTypes?: {
      id: SettingsFields;
      type: 'textfield' | 'select' | 'multi-select' | 'number' | 'switch';
      maxLength?: number;
      options?: { label: string; value: string }[];
    }[];
  };
  values?: {
    [key: string]: string;
  };
  children?: JSX.Element;
}

type SettingsForm = Record<SettingsFields, string | MultiSelectItem[]>;

export const Settings = ({ title, agencyId, fields, values, children, options }: SettingsProps) => {
  const methods = useForm<SettingsForm>();
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();

  const { control, handleSubmit, setValue } = methods;

  const { t } = useTranslation();
  const { addToast } = useSnackBar();

  const onSubmit = async (settings: SettingsForm) => {
    const payload = {} as Record<SettingsFields, string>;

    (Object.keys(settings) as SettingsFields[]).forEach((key) => {
      const value = settings[key];
      if (Array.isArray(value)) {
        const items = value.map((item) => item.value);
        payload[key] = JSON.stringify(items);
      } else {
        payload[key] = value;
      }
    });

    try {
      setIsSaving(true);
      await client.post(`settings/agency/${agencyId}`, payload);

      queryClient.invalidateQueries({ queryKey: [QueryKeys.getSettingsByAgencyId, agencyId.toString()] });
      addToast({
        message: t('agencies.settings.saveSuccess'),
        color: 'success',
      });
    } catch (e) {
      addToast({
        message: t('agencies.settings.saveError'),
        color: 'error',
      });
      console.log(e);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fields.forEach((f) => {
      if (values && values[f]) {
        const fieldTypeData = options?.fieldTypes?.find((ft) => ft.id === f);

        if (fieldTypeData && fieldTypeData.type === 'switch') {
          setValue(f, values[f]);
        } else {
          try {
            const value = JSON.parse(values[f]).map((v: string) => ({ value: v }));
            setValue(f, value);
          } catch (_) {
            setValue(f, values[f]);
          }
        }
      }
    });
  }, [fields, values, setValue, options]);

  return (
    <FormProvider {...methods}>
      <Box maxWidth={700} display="flex" flexDirection="column" gap={3}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {children && (
          <Box>
            {children}
            <Divider />
          </Box>
        )}
        <Box component="form" onSubmit={handleSubmit(onSubmit)} display="flex" flexDirection="column" gap={3}>
          {fields.map((f) => {
            const fieldTypeData = options?.fieldTypes?.find((ft) => ft.id === f);

            if (isSettingSensitive(f)) {
              return (
                <ControlledPasswordField
                  fullWidth
                  size="small"
                  key={f}
                  name={f}
                  margin="normal"
                  control={control}
                  label={t(`agencies.settings.envMap.${f}`)}
                />
              );
            } else {
              if (fieldTypeData && fieldTypeData.type === 'select') {
                return (
                  <ControlledSelect
                    variant="outlined"
                    key={f}
                    name={f}
                    control={control}
                    placeholder={t(`agencies.settings.envMap.${String(f)}`)}
                    label={t(`agencies.settings.envMap.${String(f)}`)}
                    size="small"
                    errorMessage={t('common.form.required')}
                    fullWidth
                  >
                    {fieldTypeData.options?.map((fieldOption) => (
                      <MenuItem key={fieldOption.value} value={fieldOption.value}>
                        {fieldOption.label}
                      </MenuItem>
                    ))}
                  </ControlledSelect>
                );
              } else if (fieldTypeData && fieldTypeData.type === 'multi-select') {
                return (
                  <MultiSelect
                    key={f}
                    f={f}
                    validate={(input, items) => {
                      const emailRegx = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
                      if (!emailRegx.test(input)) {
                        return t('common.form.invalidMail');
                      }

                      if (items.some((item) => item.value === input)) {
                        return t('common.form.duplicateMail');
                      }

                      return true;
                    }}
                  />
                );
              } else if (fieldTypeData && fieldTypeData.type === 'number') {
                return (
                  <Controller
                    key={f}
                    name={f}
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        size="small"
                        label={t(`agencies.settings.envMap.${String(f)}`)}
                        fullWidth
                        margin="normal"
                        onChange={(e) => {
                          const { value } = e.target;
                          if (fieldTypeData.maxLength && value.length <= fieldTypeData.maxLength) {
                            field.onChange(value);
                          } else if (!fieldTypeData.maxLength) {
                            field.onChange(value);
                          }
                        }}
                        inputProps={{
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                        }}
                      />
                    )}
                  />
                );
              } else if (fieldTypeData && fieldTypeData.type === 'switch') {
                return (
                  <Controller
                    key={f}
                    name={f}
                    control={control}
                    defaultValue="false"
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value === 'true'}
                            onChange={(e) => field.onChange(e.target.checked ? 'true' : 'false')}
                          />
                        }
                        label={t(`agencies.settings.envMap.${String(f)}`)}
                      />
                    )}
                  />
                );
              }
              return (
                <Controller
                  key={f}
                  name={f}
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      label={t(`agencies.settings.envMap.${String(f)}`)}
                      fullWidth
                      margin="normal"
                      inputProps={{ maxLength: fieldTypeData?.maxLength }}
                    />
                  )}
                />
              );
            }
          })}
          <Box display="flex" flexDirection="row-reverse">
            <LoadingButton loading={isSaving} type="submit" variant="contained" color="primary">
              {t('common.save')}
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
};
